/* tslint:disable */
/* eslint-disable */
/**
 * DigitalOrderForm
 * DigitalOrderForm backend API
 *
 * Client for the limited edition number endpoints.
 *
 * Kept in its own module instead of inside the generated api.ts, so that a future regeneration of that file
 * does not wipe it. Request plumbing and bearer token come from TypedApi, like the quota client.
 */

import { AxiosPromise } from 'axios';

import { TypedApi } from './typed-api-base';

/** Status of a number, computed by the backend from availability, assignment and administrative block. */
export type LimitedEditionStatus = 'Available' | 'Reserved' | 'Blocked';

export interface LimitedEditionNumberDto {
	id: number;
	sapCode: string;
	/** The number as it reaches SAP, so it can be anything the brand uses, e.g. "12/50". */
	number: string;
	available: boolean;
	status: LimitedEditionStatus;
	/** Why an administrator kept the number out of the pool. */
	blockedReason?: string | null;
	note?: string | null;
	/** Order holding the number, from the moment its cart is saved as a draft. */
	idOrder?: number | null;
	/** Cart row the number was picked on. */
	rowId?: number | null;
	/** "Draft" while every sap order is still a draft, otherwise the status it moved on to. */
	orderStatusCode?: string | null;
	customerName?: string | null;
	customerSapCode?: string | null;
	assignedAt?: string | null;
	assignedBy?: string | null;
	updatedAt: string;
	updatedBy?: string | null;
}

/** Payload accepted on create and update. The computed fields of LimitedEditionNumberDto are ignored. */
export interface LimitedEditionWriteDto {
	sapCode: string;
	number: string;
	available: boolean;
	blockedReason?: string | null;
	note?: string | null;
}

export interface LimitedEditionSearchResultDto {
	items: LimitedEditionNumberDto[];
	totalCount: number;
}

export interface LimitedEditionSaveResultDto {
	number: LimitedEditionNumberDto;
	error?: string | null;
	/** Set when the save went through but the administrator should know something, e.g. the order left behind. */
	warning?: string | null;
	isValid: boolean;
}

export interface LimitedEditionImportErrorDto {
	/** Row number as shown in Excel, so the user can jump straight to it. */
	rowNumber: number;
	sapCode?: string | null;
	number?: string | null;
	reason: string;
}

export interface LimitedEditionImportResultDto {
	totalRows: number;
	created: number;
	updated: number;
	skipped: number;
	errors: LimitedEditionImportErrorDto[];
	/** Rows that went through but deserve a word, typically a number already held by an order. */
	warnings: LimitedEditionImportErrorDto[];
}

/** What a bulk release of a sap code would touch, for the confirmation dialog. */
export interface LimitedEditionReleasePreviewDto {
	sapCode?: string | null;
	totalCount: number;
	availableCount: number;
	/** Held by orders still in draft. These are released by default. */
	heldByDraftsCount: number;
	/** Held by confirmed or sent orders. Released only on explicit request. */
	heldByConfirmedOrdersCount: number;
	/** Kept out of the pool by an administrator. Not touched by the release. */
	blockedCount: number;
}

export interface LimitedEditionReleaseResultDto {
	sapCode?: string | null;
	releasedCount: number;
	affectedOrdersCount: number;
	error?: string | null;
	isValid: boolean;
}

export interface LimitedEditionSearchParams {
	sapCode?: string;
	number?: string;
	status?: LimitedEditionStatus;
	orderId?: number;
	skip?: number;
	take?: number;
}

export class LimitedEditionApi extends TypedApi {
	/**
	 * @summary Search limited edition numbers (admin)
	 * @description Status, order, customer and order status come already resolved by the backend.
	 */
	public searchLimitedEditions(
		params: LimitedEditionSearchParams = {},
		options?: any,
	): AxiosPromise<LimitedEditionSearchResultDto> {
		return this.request('GET', '/admin/limitededitions', options, {
			query: {
				sapCode: params.sapCode,
				number: params.number,
				status: params.status,
				orderId: params.orderId,
				skip: params.skip,
				take: params.take,
			},
		});
	}

	/**
	 * @summary Create a number (admin)
	 * @description Answers 400 when the sap code does not exist or the number already exists for it.
	 */
	public createLimitedEdition(
		number: LimitedEditionWriteDto,
		options?: any,
	): AxiosPromise<LimitedEditionSaveResultDto> {
		return this.request('POST', '/admin/limitededitions', options, { data: number });
	}

	/**
	 * @summary Update a number (admin)
	 * @description Renumbering one held by an order is refused: it has to be released first.
	 */
	public updateLimitedEdition(
		id: number,
		number: LimitedEditionWriteDto,
		options?: any,
	): AxiosPromise<LimitedEditionSaveResultDto> {
		return this.request(
			'PUT',
			`/admin/limitededitions/${encodeURIComponent(id)}`,
			options,
			{ data: number },
		);
	}

	/**
	 * @summary Delete a number (admin)
	 * @description Refused while an order is holding it, so a sold piece cannot vanish.
	 */
	public deleteLimitedEdition(id: number, options?: any): AxiosPromise<void> {
		return this.request(
			'DELETE',
			`/admin/limitededitions/${encodeURIComponent(id)}`,
			options,
		);
	}

	/**
	 * @summary Detach a number from its order (admin)
	 * @description The number goes back in the pool. The order keeps it written on its rows and will be
	 * refused at confirmation, which is what the warning in the answer says.
	 */
	public releaseLimitedEdition(
		id: number,
		options?: any,
	): AxiosPromise<LimitedEditionSaveResultDto> {
		return this.request(
			'POST',
			`/admin/limitededitions/${encodeURIComponent(id)}/release`,
			options,
		);
	}

	/**
	 * @summary Keep a free number out of the pool, or put it back (admin)
	 * @description A null or empty reason unblocks it. Refused while an order is holding it.
	 */
	public blockLimitedEdition(
		id: number,
		reason?: string | null,
		options?: any,
	): AxiosPromise<LimitedEditionSaveResultDto> {
		return this.request(
			'POST',
			`/admin/limitededitions/${encodeURIComponent(id)}/block`,
			options,
			{ data: { reason: reason ?? null } },
		);
	}

	/**
	 * @summary Assign a free number to an existing order by hand (admin)
	 */
	public assignLimitedEdition(
		id: number,
		orderId: number,
		rowId?: number | null,
		options?: any,
	): AxiosPromise<LimitedEditionSaveResultDto> {
		return this.request(
			'POST',
			`/admin/limitededitions/${encodeURIComponent(id)}/assign`,
			options,
			{ data: { orderId, rowId: rowId ?? null } },
		);
	}

	/**
	 * @summary Import numbers from an Excel file (admin)
	 * @description Upsert: valid rows are created or updated, numbers missing from the file are left alone.
	 * A number already held by an order keeps its assignment and comes back in the warnings.
	 */
	public importLimitedEditions(
		file: File | Blob,
		options?: any,
	): AxiosPromise<LimitedEditionImportResultDto> {
		const formData = new FormData();
		formData.append('file', file);

		return this.request('POST', '/admin/limitededitions/import', options, {
			data: formData,
			contentType: 'multipart/form-data',
		});
	}

	/**
	 * @summary Export numbers to Excel (admin)
	 * @description Configuration columns plus status, order and customer. The calculated columns are ignored
	 * if the file is imported back.
	 */
	public exportLimitedEditions(options?: any): AxiosPromise<Blob> {
		return this.request('GET', '/admin/limitededitions/export', {
			responseType: 'blob',
			...options,
		});
	}

	/**
	 * @summary Download the import template (admin)
	 */
	public exportLimitedEditionsImportTemplate(options?: any): AxiosPromise<Blob> {
		return this.request('GET', '/admin/limitededitions/template', {
			responseType: 'blob',
			...options,
		});
	}

	/**
	 * @summary Count what a bulk release of a sap code would touch (admin)
	 */
	public previewLimitedEditionRelease(
		sapCode: string,
		options?: any,
	): AxiosPromise<LimitedEditionReleasePreviewDto> {
		return this.request('GET', '/admin/limitededitions/release-preview', options, {
			query: { sapCode },
		});
	}

	/**
	 * @summary Put the numbers of a sap code back in circulation (admin)
	 * @description Detaches them from their order without touching the orders, does not unblock what was
	 * kept out of the pool, deletes nothing and cannot be undone.
	 * @param includeConfirmedOrders Takes the numbers held by confirmed or sent orders too.
	 */
	public releaseLimitedEditionsBySapCode(
		sapCode: string,
		includeConfirmedOrders: boolean,
		options?: any,
	): AxiosPromise<LimitedEditionReleaseResultDto> {
		return this.request('POST', '/admin/limitededitions/release-by-sapcode', options, {
			data: { sapCode, includeConfirmedOrders },
		});
	}

	/**
	 * @summary Numbers an agent can pick for a product
	 * @description One entry per free number of any size of the product, plus the ones already held by the
	 * order being edited. An empty list means the product has no number configured, so the caller keeps the
	 * free text field.
	 * @param excludeOrderId Order being edited, whose own numbers have to stay in the list.
	 */
	public getSelectableLimitedEditions(
		productId: number,
		excludeOrderId?: number,
		options?: any,
	): AxiosPromise<LimitedEditionNumberDto[]> {
		return this.request('GET', '/limitededitions/available', options, {
			query: { productId, excludeOrderId },
		});
	}
}
